<script lang="ts">  import { m } from '$lib/paraglide/messages';

  import { translation } from '$lib/locale/locale';
  import saveIcon from '$lib/assets/shared/save.svg';
  import saveFilledIcon from '$lib/assets/shared/save-filled.svg';
  import similarIcon from '$lib/assets/search/similar.svg';
  import chevronDownIcon from '$lib/assets/shared/chevron-down.svg';
  import type { User } from '$lib/models/user';
  import { openModal } from '$lib/ui/svelte-modals';
  import Waveform from '$lib/ui/player/waveform.svelte';
  import { player } from '$lib/stores/player';
  import { timeNormalized, timeToSeconds } from '$lib/utils/time';
  import TagList from '$lib/ui/shared/tag-list.svelte';
  import { CategoryType } from '$lib/utils/category';
  import Category from '$lib/ui/shared/category.svelte';
  import { AssetType, assetLink, searchLink } from '$lib/utils/asset';
  import type { Track } from '$lib/models/track';
  import { onDestroy } from 'svelte';
  import DownloadDropdown from '$lib/ui/search/download-dropdown.svelte';
  import { browser } from '$app/environment';
  import SmallTrackThumbnail from './small-track-thumbnail.svelte';
  import { authModal } from '$lib/state/auth.svelte';
  import { wishlisted } from '$lib/state/wishlisted.svelte';
  import { api } from '$lib/api';
  import { error } from '@sveltejs/kit';
  import Spinner from '$lib/ui/shared/spinner.svelte';
  import VersionTrackWide from './version-track-wide.svelte';
  import arrowIcon from '$lib/assets/shared/right-arrow.svg';

  const { langHref } = translation();

  export let user: User | null | void;
  export let track: Track;
  export let type: AssetType;
  export let showSimilarIcon = false;
  export let freeMusicModal = false;

  let wrap: HTMLElement;
  let playing = false;
  let loadingAudio = false;
  let isActive = false;
  let progress = 0;
  let scrolled = false;

  $: versionsCount = +track.versions - 1;

  // Versions state
  let versions: Track[] = [];
  let versionsExpanded = false;
  let loadingVersions = false;

  $: if ($player.id === track.id) {
    isActive = true;
    progress = $player.progress;
    playing = $player.status === 'playing';
    loadingAudio = $player.status === 'loading';
  } else {
    isActive = false;
    progress = 0;
    playing = false;
    loadingAudio = false;
    scrolled = false;
  }

  $: if (wrap && !scrolled && $player.id === track.id) {
    scrolled = true;
    wrap.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  let trackLink = assetLink(langHref, track, type);
  let similarToLink = searchLink(langHref, type, new URLSearchParams({ similarTo: track.id.toString() }), freeMusicModal);

  let timer: any;

  function openWishlistModal() {
    if (user) {
      openModal(() => import('$lib/ui/modals/add-to-wishlist.svelte'), { track });
    } else {
      authModal.show(false);
    }
  }

  async function fetchVersions() {
    if (loadingVersions || versions.length > 0) {
      return;
    }

    loadingVersions = true;
    versionsExpanded = true;

    let endpoint = '';
    if (track.type === AssetType.Intro) {
      endpoint = `/v1/intros/${track.id}?slug=${track.slug}`;
    } else if (track.type === AssetType.Track) {
      endpoint = `/v1/tracks/${track.id}?slug=${track.slug}`;
    } else {
      throw new Error(`Asset type not supported`);
    }

    try {
      const assetFetch = await api<Track>(fetch, endpoint);
      versions = assetFetch.mp3Versions || [];
      versions = versions.slice(0, 6);
    } catch (e) {
      error(404, {
        id: '404',
        message: 'Not found',
      });
    } finally {
      loadingVersions = false;
    }
  }

  function toggleVersions() {
    if (+track.versions > 1) {
      versionsExpanded = !versionsExpanded;
      if (versionsExpanded && versions.length === 0) {
        fetchVersions();
      }
    }
  }

  if (browser) {
    onDestroy(() => {
      if (timer) {
        clearTimeout(timer);
      }
    });
  }
</script>

<div class="wrap" bind:this={wrap} class:versions-expanded={versionsExpanded}>
  <SmallTrackThumbnail {track} />
  <div class="title-column">
    <a class="title" href={trackLink}>
      {track.title}
    </a>
    <div class="mobile-info">
      <div class="time">{track.duration}</div>
      {#if track.genres?.length}
        <Category category={track.genres[0]} type={CategoryType.Genre} small={true} />
      {:else if track.moods?.length}
        <Category category={track.moods[0]} type={CategoryType.Mood} small={true} />
      {/if}
      {#if track.categories?.length && track.type === AssetType.SFX}
        <Category category={track.categories[0]} type={CategoryType.SfxCategory} small={true} />
      {/if}
    </div>
  </div>
  {#if +track.versions > 1}
    <button on:click={toggleVersions} class="versions-button" class:active={versionsExpanded}>
      +{versionsCount}
      <span>{m['track-details.versions']().toLocaleLowerCase()}</span>
      <img class="chevron" src={chevronDownIcon} alt={m['track-details.versions']()} class:rotated={versionsExpanded} loading="lazy" />
    </button>
  {/if}
  <div class="wave">
    <Waveform
      data={track.waveform}
      duration={timeToSeconds(track.duration)}
      {progress}
      height={47}
      on:seek={(e) => player.play(track, e.detail)}
    />
  </div>

  <div class="vertical">
    <div class="time">{timeNormalized(track.duration)}</div>
    {#if track.tempo}
      <div class="bpm">{m['common.bpm']()}: {track.tempo}</div>
    {/if}
  </div>

  <div class="vertical tags">
    <div class="categories">
      {#if track.genres?.length}
        <Category category={track.genres[0]} type={CategoryType.Genre} />
      {/if}
      {#if track.moods?.length}
        <Category category={track.moods[0]} type={CategoryType.Mood} />
      {/if}
      {#if track.categories?.length && track.type === AssetType.SFX}
        <Category category={track.categories[0]} type={CategoryType.SfxCategory} />
      {/if}
    </div>
    <TagList noFollow={true} tags={track.tags} type={track.type} />
  </div>
  {#if showSimilarIcon}
    <a rel="nofollow" class="icon similar small" data-tooltip={m['search.find-similar-tracks']()} href={similarToLink}>
      <img class="img_text-secondary" src={similarIcon} alt="Similar tracks" loading="lazy" />
    </a>
  {/if}

  <button on:click={openWishlistModal} data-tooltip={m['wishlist.title']()} class="icon small">
    {#if wishlisted.ids.includes(track.id)}
      <img class="img_magic-potion" src={saveFilledIcon} alt={m['actions.save']()} loading="lazy" />
    {:else}
      <img class="img_text-secondary" src={saveIcon} alt={m['actions.save']()} loading="lazy" />
    {/if}
  </button>

  <DownloadDropdown {track} {user} {freeMusicModal} />
</div>

{#if versionsExpanded && +track.versions > 1}
  <div class="versions-section">
    {#if loadingVersions}
      <div class="loading-versions">
        <Spinner size={'20px'} micro={true} />
        <span class="loading-text">{m['search.loading-versions']()}</span>
      </div>
    {:else if versions.length > 0}
      <div class="versions-list">
        {#each versions as version}
          <div class="version-item">
            <VersionTrackWide asset={version} mainAsset={track} {user} />
          </div>
        {/each}
        {#if versions.length < versionsCount}
          <a href={trackLink} class="more-versions">
            <div>{m['track-details.more-versions']()}</div>
            <img class="img_text-secondary" src={arrowIcon} alt="" aria-hidden="true" loading="lazy" />
          </a>
        {/if}
      </div>
    {:else}
      <div class="no-versions">
        {m['track-details.no-loops']()}
      </div>
    {/if}
  </div>
{/if}

<style>
  :root {
    --version-player-height: 62px;
  }

  @media (max-width: 900px) {
    :root {
      --version-player-height: 50px;
    }
  }

  .wrap {
    width: 100%;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding: 9px 16px;
    position: relative;
    content-visibility: auto;
    contain-intrinsic-height: auto 89px;
    contain-intrinsic-width: auto 100%;
    height: 89px;
  }

  .wrap.versions-expanded {
    background-color: #eff0f0;
  }

  @media (hover) {
    .wrap:hover {
      background-color: var(--cultured);
    }
  }

  .title-column {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    width: 135px;
    margin-right: 28px;
    margin-left: 16px;
    justify-content: center;
  }

  .title {
    color: var(--text-primary);
    font-weight: 500;
    font-size: 14px;
  }

  .title:hover {
    text-decoration: none;
  }

  .vertical {
    flex-shrink: 0;
    margin-left: 28px;
  }

  .time {
    font-size: 14px;
    width: 68px;
    color: var(--text-primary);
  }

  .bpm {
    font-size: 14px;
    width: 68px;
    margin-top: 8px;
    color: var(--text-secondary);
  }

  .wave {
    flex-grow: 1;
  }

  .categories {
    width: 208px;
    margin-bottom: 8px;
  }

  .icon {
    flex-shrink: 0;
    height: 24px;
    width: 24px;
  }

  .icon + .icon {
    margin-left: 16px;
  }

  .icon img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  .mobile-info {
    display: none;
  }

  /* Versions button styles */
  .versions-button {
    background-color: var(--surface-grey);
    border-radius: 8px;
    border: 1px solid var(--border-100, #dee0e3);
    transition: background-color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 2px 4px;
    white-space: nowrap;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    margin-right: 28px;
  }

  .versions-button.active {
    background-color: var(--cultured);
  }

  .chevron {
    transition: transform 0.3s ease;
    width: 12px;
    height: 12px;
    object-fit: contain;
  }

  .chevron.rotated {
    transform: rotate(180deg);
  }

  .versions-section {
    width: 100%;
    background-color: #fafafa;
    animation: slideDown 0.3s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .loading-versions {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    gap: 12px;
    height: var(--version-player-height);
  }

  .loading-text {
    font-size: var(--sm-font-size);
    color: var(--text-secondary);
  }

  .versions-list {
    display: flex;
    flex-direction: column;
    animation: slideDown 0.3s ease-out;
  }

  .more-versions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 2px 4px;
    color: var(--text-primary);
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    margin-top: 12px;
    margin-bottom: 20px;
    margin-left: auto;
    margin-right: auto;
  }

  .more-versions img {
    width: 12px;
    height: 12px;
    object-fit: contain;
    margin-left: 4px;
  }

  .no-versions {
    text-align: center;
    padding: 24px;
    color: var(--text-secondary);
    font-size: var(--sm-font-size);
  }

  @media (max-width: 1450px) {
    .versions-button span {
      display: none;
    }
  }

  @media (max-width: 900px) {
    .vertical {
      display: none;
    }

    .title-column {
      width: auto;
      flex-grow: 0;
      margin-left: 13px;
    }

    .vertical.tags {
      margin-left: 13px;
    }

    .title {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 130px;
      width: 130px;
    }
    .wave {
      margin-right: 30px;
    }

    .mobile-info {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 130px;
    }

    .mobile-info .time {
      width: auto;
      margin: 0;
      color: var(--black-coral);
      font-size: 12px;
      margin-right: 8px;
    }
    .more-versions {
      margin-top: 4px;
      margin-bottom: 14px;
    }

    .wrap {
      padding: 9px 0;
    }

    .versions-button {
      margin-right: 10px;
    }

    .icon + .icon {
      margin-left: 10px;
    }
  }

  @media (max-width: 620px) {
    .wrap {
      height: 69px;
      contain-intrinsic-height: auto 69px;
    }
    .title {
      max-width: 120px;
      width: auto;
    }

    .wave {
      display: none;
    }

    .title-column {
      margin-left: 10px;
      margin-right: 10px;
      flex-grow: 1;
    }
  }
</style>
